// ═══════════════════════════════════════════════════════════════
// Intake helpers — ported from IntakeUnited Firebase app
// ═══════════════════════════════════════════════════════════════

// Shared character set (excludes I, O, 0, 1 to avoid ambiguity)
const CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

// ── Tag code generator ───────────────────────────────────────
export function generateTagCode(): string {
  const prefix = 'IU';
  const part = Array.from({ length: 6 }, () => CHARS[Math.floor(Math.random() * CHARS.length)]).join('');
  return `${prefix}-${part}`;
}

// ── Property item code generator ─────────────────────────────
export function generateItemCode(): string {
  let code = '';
  for (let i = 0; i < 4; i++) code += CHARS[Math.floor(Math.random() * CHARS.length)];
  return `IU-P-${code}`;
}

// ── Calculate age from DOB string (YYYY-MM-DD) ──────────────
export function calcAge(dobStr: string): number | null {
  if (!dobStr) return null;
  const dob = new Date(dobStr);
  if (isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age;
}

// ── Date formatting ──────────────────────────────────────────
export function formatDate(ts: string | Date | null): string {
  if (!ts) return '\u2014';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTime(ts: string | Date | null): string {
  if (!ts) return '\u2014';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ── Parse AAMVA PDF417 barcode data (driver licenses) ────────
function formatAAMVADate(raw: string): string {
  if (!raw || raw.length < 8) return raw;
  // MMDDYYYY -> YYYY-MM-DD
  if (raw.length === 8) {
    return `${raw.slice(4)}-${raw.slice(0, 2)}-${raw.slice(2, 4)}`;
  }
  return raw;
}

export function parseAAMVA(rawText: string): Record<string, string> {
  const result: Record<string, string> = {};
  if (!rawText) return result;

  const lines = rawText.split(/[\n\r]+/);
  for (const line of lines) {
    const key = line.substring(0, 3);
    const val = line.substring(3).trim();
    switch (key) {
      case 'DCS': result.lastName = val; break;
      case 'DAC': result.firstName = val; break;
      case 'DAD': result.middleName = val; break;
      case 'DBB': result.dob = formatAAMVADate(val); break;
      case 'DAG': result.address = val; break;
      case 'DAI': result.city = val; break;
      case 'DAJ': result.state = val; break;
      case 'DAK': result.zip = val.substring(0, 5); break;
      case 'DAQ': result.licenseNum = val; break;
      case 'DBA': result.expiration = formatAAMVADate(val); break;
      case 'DAU': result.height = val; break;
      case 'DAY': result.eyeColor = val; break;
      case 'DBC': result.sex = val === '1' ? 'M' : val === '2' ? 'F' : 'Other'; break;
    }
  }
  return result;
}

// ── Greeting ─────────────────────────────────────────────────
export function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// ── Constants ────────────────────────────────────────────────

export const TRIAGE_COLORS: Record<string, string> = {
  green: '#16a34a',
  yellow: '#d97706',
  red: '#dc2626',
};

export const TRIAGE_LABELS: Record<string, string> = {
  green: 'Minor / Delayed',
  yellow: 'Delayed Care',
  red: 'Immediate',
};

export const DESTINATIONS = [
  { key: 'reunified_family', label: 'Reunified with Family', icon: 'fa-people-group', color: 'green' },
  { key: 'hospital', label: 'Transported to Hospital', icon: 'fa-hospital', color: 'red' },
  { key: 'self_release', label: 'Self-Released', icon: 'fa-walking', color: 'blue' },
  { key: 'law_enforcement', label: 'Released to Law Enforcement', icon: 'fa-shield', color: 'navy' },
  { key: 'shelter', label: 'Transferred to Shelter', icon: 'fa-house-chimney', color: 'yellow' },
  { key: 'other_agency', label: 'Other Agency', icon: 'fa-building', color: 'gray' },
  { key: 'ems', label: 'EMS / Ambulance', icon: 'fa-truck-medical', color: 'red' },
  { key: 'other', label: 'Other', icon: 'fa-ellipsis', color: 'gray' },
] as const;

export const CONDITIONS = [
  { key: 'ambulatory', label: 'Ambulatory / Walking' },
  { key: 'stable', label: 'Stable' },
  { key: 'injured', label: 'Injured' },
  { key: 'critical', label: 'Critical' },
  { key: 'unknown', label: 'Unknown' },
] as const;

export const PROPERTY_CATEGORIES = [
  { key: 'bag', label: 'Bag / Backpack', icon: 'fa-bag-shopping' },
  { key: 'purse', label: 'Purse / Wallet', icon: 'fa-wallet' },
  { key: 'electronics', label: 'Electronics', icon: 'fa-laptop' },
  { key: 'vehicle', label: 'Vehicle', icon: 'fa-car' },
  { key: 'clothing', label: 'Clothing', icon: 'fa-shirt' },
  { key: 'documents', label: 'Documents / ID', icon: 'fa-id-card' },
  { key: 'medical', label: 'Medical Equipment', icon: 'fa-kit-medical' },
  { key: 'keys', label: 'Keys', icon: 'fa-key' },
  { key: 'athletic', label: 'Athletic Equipment', icon: 'fa-bicycle' },
  { key: 'baby', label: 'Baby / Stroller', icon: 'fa-baby-carriage' },
  { key: 'other', label: 'Other', icon: 'fa-box' },
] as const;

// ── Resize a base64 dataURL down to a small thumbnail ────────
// Used to fit photos inside Ignatius TEXT fields (~65KB cap)
export const resizeBase64ToThumb = (dataUrl: string, maxDim = 400, quality = 0.55): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });

// ── Image resize to base64 (for photo upload) ────────────────
export const resizeImageToBase64 = (file: File, maxDim = 1200): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
